(ns practitest.frontend.project-step-params-settings.index
  (:require
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.re-frame :refer [partial-subscribe partial-dispatch-sync]]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.list-builder :as list-builder]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.navigation :as navigation]
    [practitest.frontend.components.project-settings-tabs :as project-settings-tabs]
    [practitest.frontend.components.selector :as selector]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.grid :as grid2]
    [practitest.frontend.components2.grid.action-cells :as action-cells]
    [practitest.frontend.components2.grid.common :as gc]
    [practitest.frontend.forms3.entity-form.forms :as forms3]
    [practitest.frontend.project-step-params-settings.events :as events]))

(def ^:private table-id "project-step-params-index")
(def ^:private default-per-page 100)

(def ^:private type-labels
  {"text"     (get-translation "integration-field-mapping.field-type.text" "Text")
   "list"     (get-translation "integration-field-mapping.field-type.list" "List")
   "url_list" (get-translation "step-parameters.type.url-list" "URL List")})

(defn- type-label [type]
  (get type-labels type type))

;; Name opens the edit modal, so it needs its own type instead of :internal-url.
(gc/register-field-type!
  :step-param-name
  gc/header-title
  (fn [{:keys [field]} row] (str (get row field)))
  (fn [{:keys [field]} row _]
    [:a {:href     "#"
         :on-click (fn [e]
                     (.preventDefault e)
                     (re-frame/dispatch [::events/open-edit-modal row]))}
     (get row field)]))

(defn- rows []
  (mapv (fn [row]
          (assoc row :val_display (if (str/blank? (:val row)) "–" (:val row))
                     :type_label (type-label (:type row))))
        @(re-frame/subscribe [::events/rows])))

(defn- columns []
  [{:field         :name
    :header        (get-translation "generic.label.name" "Name")
    :type          :step-param-name
    :default-width 180
    :sortable?     true
    :autofilter?   true}
   {:field         :val_display
    :header        (get-translation "generic.label.default-value" "Default Value")
    :type          :text
    :default-width 220
    :sortable?     true
    :autofilter?   true}
   {:field           :type_label
    :header          (get-translation "custom-fields.type" "Type")
    :type            :list
    :multiple?       true
    :possible-values (vals type-labels)
    :default-width   120
    :sortable?       true
    :autofilter?     true}
   {:field         :created_at
    :header        (get-translation "generic.label.created" "Created")
    :type          :date-time
    :default-width 160
    :sortable?     true
    :autofilter?   true}
   {:field         :updated_at
    :header        (get-translation "custom-fields.modified" "Modified")
    :type          :date-time
    :default-width 160
    :sortable?     true
    :autofilter?   true}])

(defn- action-cell [row]
  [action-cells/action-cells
   [{:label    (get-translation "generic.button.edit" "Edit")
     :icon     icons/mux-pencil
     :on-click #(re-frame/dispatch [::events/open-edit-modal row])}
    {:label        (get-translation "generic.button.delete" "Delete")
     :icon         icons/mux-trash
     :on-click     #(re-frame/dispatch [::events/delete-param (:key_name row)])
     :confirmation {:title (get-translation "step-parameters.delete.title" "Delete Step Parameter?")
                    :body  (get-translation "step-parameters.delete.body"
                                              "This step parameter will be deleted permanently.")
                    :destructive? true}}]])

(defn- post-columns []
  [{:header        (get-translation "generic.label.actions" "Actions")
    :field         :actions
    :default-width 80
    :width         80
    :type          :custom
    :text-align    :center
    :resizable?    false
    :sortable?     false
    :systemcol?    true
    :class         ["action-menu"]
    :cell-fn       (fn [_ row _] [action-cell row])}])

;; ---- create / edit modal ----

(defn- field [label & body]
  (into [:div.g2-field-container [:label.m-label label]] body))

(def ^:private possible-values-form-props
  {:get-field-state (partial-subscribe [::forms3/field-state events/possible-values-form-id])
   :set-field-state (partial-dispatch-sync [::forms3/set-field-state events/possible-values-form-id])})

(defn- possible-values []
  (:value @((:get-field-state possible-values-form-props) events/possible-values-field-id)))

(defn- possible-values-editor []
  [:div.g2-field-container
   [:label.m-label.top (get-translation "step-parameters.form.possible-values" "Possible Values")]
   [list-builder/view-panel events/possible-values-field-id possible-values-form-props
    (fn [value] (re-frame/dispatch [::events/delete-possible-value value]))
    (fn [_])]])

(defn- default-value-field [{:keys [type val]}]
  (field (get-translation "generic.label.default-value" "Default Value")
    (if (contains? events/list-types type)
      [selector/new-select
       {:items      (possible-values)
        :label-fn   identity
        :key-fn     identity
        :clearable? true
        :value      (when-not (str/blank? val) val)
        :on-change  (fn [value] (re-frame/dispatch [::events/set-modal-field :val (or value "")]))}]
      [:input.search-input
       {:value     (or val "")
        :on-change #(re-frame/dispatch [::events/set-modal-field :val (dom/event-value %)])}])))

(defn- step-param-modal []
  (let [{:keys [mode name type saving?] :as modal-state} @(re-frame/subscribe [::events/modal])
        edit?     (= mode :edit)
        list-type? (contains? events/list-types type)
        disabled? (or saving?
                      (str/blank? name)
                      (and list-type? (empty? (possible-values))))]
    [modal/bootstrap-modal
     {:is-open?         (some? modal-state)
      :on-request-close #(re-frame/dispatch [::events/close-modal])
      :close-button?    true
      :centered?        true
      :class            "step-param-modal"}
     [:span (if edit?
              (get-translation "step-parameters.edit.title" "Update Step Parameter")
              (get-translation "step-parameters.new" "New Step Parameter"))]
     (when modal-state
       [:<>
        (field (get-translation "generic.label.name" "Name")
          [:input.search-input
           {:value     (or name "")
            :autoFocus true
            :disabled  saving?
            :on-change #(re-frame/dispatch [::events/set-modal-field :name (dom/event-value %)])}])
        (field (get-translation "step-parameters.form.format" "Format")
          [selector/new-select
           {:items     ["text" "list" "url_list"]
            :label-fn  type-label
            :key-fn    identity
            :value     type
            :disabled? saving?
            :on-change (fn [value] (re-frame/dispatch [::events/set-modal-field :type value]))}])
        (default-value-field modal-state)
        (when list-type?
          [possible-values-editor])])
     [:div.add-cancel-component
      [buttons/button {:text     (get-translation "generic.button.cancel" "Cancel")
                       :disabled? saving?
                       :on-click #(re-frame/dispatch [::events/close-modal])}]
      [buttons/button {:text      (get-translation "generic.button.save" "Save")
                       :primary?  true
                       :disabled? disabled?
                       :on-click  #(re-frame/dispatch [::events/save-modal])}]]]))

;; ---- grid ----

(defn- toolbar [_grid-props]
  [:div.grid-toolbar-right.project-fields-toolbar
   [buttons/button {:primary?  true
                    :text      (get-translation "step-parameters.new" "New Step Parameter")
                    :on-click  #(re-frame/dispatch [::events/open-create-modal])}]])

(defn- params-grid []
  (let [grid-state (reagent/atom {:module table-id})]
    (fn []
      (let [menu-collapsed? @(re-frame/subscribe [::navigation/project-level-menu-collapsed?])]
        [grid2/grid
         {:columns              (columns)
          :post-columns         (post-columns)
          :full-data?           true
          :data                 (rows)
          :loading?             @(re-frame/subscribe [::events/loading?])
          :resizable?           true
          :expanded?            menu-collapsed?
          :on-expand-click      (fn [] (re-frame/dispatch [::navigation/set-project-level-menu-collapsed? (not menu-collapsed?)]))
          :table-id             table-id
          :grid-state           grid-state
          :local-state-updating grid-state
          :grid-toolbar         toolbar
          :columns-widths-save  (reagent/partial grid-storage/save-column-sizes table-id)
          :columns-widths-load  (reagent/partial grid-storage/load-column-sizes table-id)
          :sort-order-save      (reagent/partial grid-session-storage/save-sort-order table-id)
          :sort-order-load      (reagent/partial grid-session-storage/load-sort-order table-id)
          :per-page-save        (reagent/partial grid-storage/save-per-page table-id)
          :per-page-load        #(or (grid-storage/load-per-page table-id) default-per-page)
          :current-page-save    (reagent/partial grid-storage/save-current-page table-id)
          :current-page-load    (reagent/partial grid-storage/load-current-page table-id)}]))))

(defn index []
  ;; Reuses the project-fields-* page shell classes.
  [:div.project-fields-index-page
   [step-param-modal]
   [:div.project-fields-header
    [project-settings-tabs/project-settings-tabs {}]
    [:h1.project-fields-page-title
     (get-translation "settings_sub_navbar.step_parameters" "Step Parameters")]]
   [:div.project-fields-content
    [params-grid]]])

(def main-controller
  {:start (fn [_] (re-frame/dispatch [::events/load!]))
   :stop  (fn [_] (re-frame/dispatch [::events/reset]))})
